import { viewportHeight as layoutViewportHeight, viewportScrollY } from "../utils/displayDensity";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function MgSkeleton() {
  return (
    <div className="mg-card mg-card-wrap" style={{ minHeight: 190 }}>
      <div className="mg-base" />
      <div
        className="mg-card-inner"
        style={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: "100%",
            aspectRatio: "230 / 107",
            background:
              "linear-gradient(90deg, #151515 25%, #1f1f1f 50%, #151515 75%)",
            backgroundSize: "200% 100%",
            animation: "mgShimmer 1.4s infinite",
          }}
        />
        <div
          style={{
            height: 42,
            background:
              "linear-gradient(90deg, #0e0e0e 25%, #141414 50%, #0e0e0e 75%)",
            backgroundSize: "200% 100%",
            animation: "mgShimmer 1.6s infinite",
          }}
        />
      </div>
    </div>
  );
}

export default function VirtualizedGameGrid({
  games,
  onRemoveGame,
  totalCcu,
  maxCcu,
  loading = false,
  onCardMouseMove, // pass Steam's tilt handler
  onCardMouseLeave, // pass Steam's tilt handler
}) {
  const containerRef = useRef(null);

  // Simple viewport-based windowing
  const calcVisible = () => {
    const cardH = 300; // generous height for popout room
    const rows = Math.ceil(layoutViewportHeight() / cardH) + 1;
    const cols = 5;
    return rows * cols;
  };

  const [visibleCount, setVisibleCount] = useState(calcVisible());

  useEffect(() => {
    setVisibleCount(calcVisible());
  }, [games.length]);

  useEffect(() => {
    const onResize = () => setVisibleCount(calcVisible());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (
        layoutViewportHeight() + viewportScrollY() + 400 >=
        document.documentElement.offsetHeight
      ) {
        setVisibleCount((prev) => Math.min(prev + 12, games.length));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [games.length]);

  const displayGames = loading ? [] : games.slice(0, visibleCount);
  const skeletonCount = loading ? Math.max(60, visibleCount) : 0;

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {/* Shared styles (same as Steam) */}
      <style>{`
        @keyframes mgShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .mg-grid { 
          display: grid; 
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); 
          gap: 24px; 
          overflow: visible;
          position: relative;
        }
        .mg-card { position: relative; perspective: 900px; transform-style: preserve-3d; z-index: 0; }
        .mg-card:hover { z-index: 1000; }
        .mg-base {
          position: absolute; inset: 0; transform: translateZ(-22px);
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(0,153,255,0.18), rgba(0,153,255,0.02));
          box-shadow: 0 18px 40px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(63,169,245,0.12);
          filter: saturate(110%);
        }
        .mg-card-inner {
          position: relative;
          border-radius: 14px;
          transform: rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
          transition: transform 140ms ease, box-shadow 140ms ease, translate 140ms ease;
          will-change: transform, box-shadow;
          box-shadow: 0 10px 28px rgba(0,0,0,0.55);
          overflow: hidden;
          background: #000;
          display: block;
        }
        .mg-card:hover .mg-card-inner { box-shadow: 0 18px 44px rgba(0,0,0,0.65); }
        .mg-img { display: block; width: 100%; height: auto; aspect-ratio: 230 / 107; object-fit: cover; }
        .mg-rank {
          position: absolute; top: 8px; left: 8px; padding: 2px 6px;
          font-size: 13px; font-weight: 700; color: #fff;
          background: rgba(0,0,0,0.55); border-radius: 4px; z-index: 2; backdrop-filter: blur(2px);
        }
        .mg-footer {
          position: absolute; left: 0; right: 0; bottom: 0; z-index: 2;
          padding: 12px 14px 14px 14px; display: flex; gap: 12px; align-items: baseline; justify-content: space-between;
          background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 30%, rgba(0,0,0,0.78) 100%);
        }
        .mg-title {
          font-weight: 800; font-size: 15px; color: #fff; max-width: 72%;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .mg-info-title {
          position: relative; margin: 0 0 11px; padding: 0 0 9px 13px;
          color: #fff; font-size: 15px; font-weight: 900; line-height: 1.2;
          letter-spacing: 0.08em; text-transform: uppercase;
          text-shadow: 0 1px 10px rgba(63,169,245,0.22);
        }
        .mg-info-title::before {
          content: ""; position: absolute; left: 0; top: 1px; bottom: 10px;
          width: 3px; border-radius: 3px;
          background: linear-gradient(180deg, #75d5ff, #3fa9f5 55%, #2672d8);
          box-shadow: 0 0 12px rgba(63,169,245,0.65);
        }
        .mg-info-title::after {
          content: ""; position: absolute; left: 13px; right: 0; bottom: 0;
          height: 1px; background: linear-gradient(90deg, rgba(63,169,245,0.58), rgba(63,169,245,0));
        }
        .mg-stats { font-size: 13px; color: #cfd8e3; white-space: nowrap; font-weight: 600; }
        .mg-shine {
          position: absolute; inset: 0; pointer-events: none; opacity: 0; transition: opacity 150ms ease;
          background:
            radial-gradient(600px at var(--sx, 50%) var(--sy, 50%), rgba(255,255,255,0.18), transparent 40%),
            linear-gradient(120deg, rgba(255,255,255,0.12), rgba(63,169,245,0.12) 20%, transparent 60%);
          mix-blend-mode: screen; z-index: 1;
        }
        .mg-card:hover .mg-shine { opacity: 1; }
        .mg-card:hover { transform: translateY(-1px); }
        .mg-remove {
          position: absolute; top: 8px; right: 8px; z-index: 3;
          background: rgba(255,255,255,0.2); border: none; border-radius: 50%; width: 24px; height: 24px;
          color: #fff; font-weight: bold; cursor: pointer;
        }
        .mg-info {
          position: absolute; top: 100%; left: 0; right: 0;
          background: rgba(34,34,34,0.96); border-radius: 12px; padding: 10px 12px; margin-top: 6px;
          opacity: 0; pointer-events: none; transition: opacity 160ms ease; z-index: 2000;
          font-size: 14px; line-height: 1.4; border: 1px solid #333;
        }
        .mg-card:hover .mg-info { opacity: 1; }
        .mg-card-wrap { position: relative; }
      `}</style>

      <div className="mg-grid">
        {loading &&
          Array.from({ length: skeletonCount }).map((_, i) => (
            <MgSkeleton key={i} />
          ))}

        {!loading &&
          displayGames.map((g, i) => {
            const share =
              totalCcu > 0 ? ((g.ccu / totalCcu) * 100).toFixed(2) : "0.00";
            return (
              <div
                key={g.app_id}
                className="mg-card mg-card-wrap"
                onMouseMove={onCardMouseMove}
                onMouseLeave={onCardMouseLeave}
              >
                <button
                  className="mg-remove"
                  onClick={() => onRemoveGame?.(g.app_id)}
                  aria-label="Remove game"
                >
                  ×
                </button>

                <div className="mg-base" />
                <Link
                  to={`/game/${g.app_id}`}
                  className="mg-card-inner"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <img className="mg-img" src={g.box_art} alt={g.name} />
                  <div className="mg-rank">#{i + 1}</div>
                  <div className="mg-footer">
                    <div className="mg-stats">
                      {g.ccu?.toLocaleString()} · {share}%
                    </div>
                  </div>
                  <div className="mg-shine" />
                </Link>

                {/* Metadata popout */}
                <div className="mg-info">
                  <div className="mg-info-title">{g.name}</div>
                  {g.short_description && (
                    <div style={{ marginBottom: 10 }}>
                      {g.short_description}
                    </div>
                  )}

                  {(g.release_date || g.full_price) && (
                    <div style={{ marginBottom: 6 }}>
                      {g.release_date && (
                        <span>
                          <strong>Release:</strong>{" "}
                          {new Date(g.release_date).toLocaleDateString()}
                        </span>
                      )}
                      {g.release_date && g.full_price && <span> / </span>}
                      {g.full_price && (
                        <span>
                          <strong>Price: $</strong> {g.full_price}
                        </span>
                      )}
                    </div>
                  )}

                  {g.publishers && (
                    <div style={{ marginBottom: 6 }}>
                      <strong>Publishers:</strong> {g.publishers}
                    </div>
                  )}

                  {g.developers && (
                    <div style={{ marginBottom: 10 }}>
                      <strong>Developer(s):</strong> {g.developers}
                    </div>
                  )}

                  {Array.isArray(g.tags) && g.tags.length > 0 && (
                    <div>
                      <strong>Tags:</strong> {g.tags.join(", ")}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {!loading && !games.length && (
        <div style={{ textAlign: "center", marginTop: 40, color: "#aaa" }}>
          No games found.
        </div>
      )}
    </div>
  );
}
